import { resourceGetList, resourcePatchDetail, resourcePatchList, sendAction } from "shared/djangio/action-creators"
import { sendUserAction } from "shared/user-actions/user-actions-action-creators"
import * as actionTypes from "shared/favorites/action-types"

/**
 *                              A Little Bit About Favorites:
 *                                         A Novel
 *
 * Chapter 1: Getting into the headspace
 *     Users do things. We save some of these actions made by users with UserAction. And sometimes we show these
 *     to the user so they can replay their action and also 'favorite' it so they can replay it many times.
 *
 * Chapter 2: What actions do we show? What actions can we meaningfully favorite? And where is that info then stored?
 *     UserActionType.search_created    (search is stored in safedsearch fk)
 *     UserActionType.profile_visited   (profile is stored in corresponding fk, e.g. supporter, document, etc.)
 *
 * We use the UserAction field `is_favorited` to determine whether a UserAction has been favorited or nah.
 *
 *                                           THE END
 */

/**
 * Loads all of the most recent UserActions that this user can replay.
 * This uses two separate request so that we get a minimum of 20 of each
 * kind to put in the two lists we display.
 * @param  {Boolean} is_favorited [Whether to get favorited UAs or not]
 */
export const loadUserActions =
    ({ kwargs } = {}) =>
    (dispatch) => {
        dispatch(
            sendAction(DjangIO.app.useractions.models.UserAction.objects, actionTypes.loadUserActionsTypes, {
                action: "recent_and_favorite_searches_and_profiles",
                kwargs,
                method: "get",
            }),
        )
    }

export const favoriteUserAction = (userAction) =>
    resourcePatchDetail(
        DjangIO.app.useractions.models.UserAction,
        actionTypes.favoriteUserActionTypes,
        userAction.id,
        { is_favorited: true },
        { userAction },
    )

export const unfavoriteUserAction = (userAction) =>
    resourcePatchDetail(
        DjangIO.app.useractions.models.UserAction,
        actionTypes.unfavoriteUserActionTypes,
        userAction.id,
        { is_favorited: false },
        { userAction },
    )

export const archiveUserAction = (userAction) =>
    resourcePatchDetail(
        DjangIO.app.useractions.models.UserAction,
        actionTypes.archiveUserActionTypes,
        userAction.id,
        { archived: true },
        { userAction },
    )

export const clearRecents = () =>
    sendAction(DjangIO.app.useractions.models.UserAction, actionTypes.clearRecentsTypes, {
        action: "archive_all_searches",
        method: "get",
    })

/**
 * Returns the useractions associated with the profile
 */
export const loadProfileUserActions = (profileId, profileQdt) =>
    sendAction(DjangIO.app.useractions.models.UserAction.objects, actionTypes.loadUserActionsForProfileTypes, {
        action: "get_favorited_or_recent_related_user_action",
        kwargs: {
            qdt: profileQdt,
            id: profileId,
        },
        payload: {
            profileId,
            profileQdt,
        },
        method: "get",
    })

/**
 * Patches/creates user action corresponding to a profile visit with the profile marked as a favorite
 * @param  {number} profileId
 * @param  {number} profileQdt          [QDT value]
 * @param  {number|undefined} recentUserActionId  [user action id or undefined if there is no
 *                                                 useraction associated with profile yet]
 */
export const favoriteProfile = (profileId, profileQdt, recentUserActionId) => (dispatch) => {
    // if we have a useraction associated with this profile, then just patch that
    if (recentUserActionId) {
        dispatch(
            resourcePatchDetail(
                DjangIO.app.useractions.models.UserAction,
                actionTypes.favoriteProfileTypes,
                recentUserActionId,
                { is_favorited: true },
                { profileId, profileQdt },
            ),
        )
    } else {
        // otherwise, we need to create a new useraction with is_favorited=True
        dispatch({
            type: actionTypes.favoriteProfileTypes.start,
            profileId,
            profileQdt,
        })
        dispatch(
            sendUserAction(
                DjangIO.app.useractions.types.UserActionType.profile_visited,
                `${DjangIO.app.models.QuorumDataType.by_value(profileQdt).singular} Marked as Favorite in Profile`,
                null,
                profileQdt,
                DjangIO.newResourceFromDataType(profileQdt).foreignKey(profileId),
                true,
                true, // forceSendAction
            ),
        )
            .then((response) =>
                dispatch({
                    type: actionTypes.favoriteProfileTypes.success,
                    profileId,
                    profileQdt,
                    response,
                }),
            )
            .catch((err) =>
                dispatch({
                    type: actionTypes.favoriteProfileTypes.fail,
                }),
            )
    }
}

export const unfavoriteProfile = (profileId, profileQdt) =>
    sendAction(DjangIO.app.useractions.models.UserAction.objects, actionTypes.unfavoriteProfileTypes, {
        action: "unfavorite_all_related_useractions",
        kwargs: {
            qdt: profileQdt,
            id: profileId,
        },
        payload: {
            profileId,
            profileQdt,
        },
        method: "get",
    })

export const clearSlice = () => ({
    type: actionTypes.actionTypes.CLEAR,
})
